import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { IconButton, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import { useState, type MouseEvent } from 'react';
import { exportLabels } from '../../constants/exportLabels';
import { useDownloadExport } from '../../hooks/useDataExport';
import { useReportContext } from '../../hooks/useReports';
import { canExport } from '../../utils/reports';
import ExportCsvDialog from './ExportCsvDialog';

interface ExportDataButtonProps {
  projectId: string;
  /** Edits not saved yet: the export would not reflect them. */
  hasUnsavedChanges: boolean;
}

const ExportDataButton = ({ projectId, hasUnsavedChanges }: ExportDataButtonProps) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [csvOpen, setCsvOpen] = useState(false);
  const { context, isError, refreshIfOld } = useReportContext(projectId);
  const download = useDownloadExport(projectId);
  const availability = canExport(context, hasUnsavedChanges, isError);
  const isDisabled = !availability.enabled || download.isPending;

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    if (!isDisabled) setAnchor(event.currentTarget);
  };

  const handleExcel = () => {
    setAnchor(null);
    download.mutate({ format: 'xlsx' });
  };

  const handleCsv = () => {
    setAnchor(null);
    setCsvOpen(true);
  };

  return (
    <>
      {/* aria-disabled instead of disabled keeps the button focusable, so keyboard users get the reason too. */}
      <Tooltip title={download.isPending ? exportLabels.downloading : availability.reason ?? exportLabels.menu_button} describeChild>
        <IconButton
          aria-label={exportLabels.menu_button}
          aria-disabled={isDisabled}
          aria-haspopup="menu"
          onClick={handleOpen}
          onMouseEnter={refreshIfOld}
          onFocus={refreshIfOld}
          loading={download.isPending}
          sx={isDisabled ? { color: 'action.disabled', cursor: 'default' } : undefined}
        >
          <FileDownloadIcon />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <MenuItem onClick={handleExcel}>
          <ListItemText>{exportLabels.menu_excel}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCsv}>
          <ListItemText>{exportLabels.menu_csv}</ListItemText>
        </MenuItem>
      </Menu>
      <ExportCsvDialog projectId={projectId} open={csvOpen} onClose={() => setCsvOpen(false)} />
    </>
  );
};

export default ExportDataButton;
